import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  canEditOptionsForActivity,
  isActualCloseDateField,
  isOwnerField,
  isStageField,
} from 'checks/fields';
import useField from 'hooks/useField';
import Switch from 'components/Kizen/Switch';
import Button from 'components/Button';
import { useAsync } from 'react-use';
import TextInput from 'components/Inputs/TextInput';
import { isPipelineObject, isClientObject } from 'components/Modals/utilities';
import {
  getCustomObjectsSettingsAccess,
  getContactsAccess,
} from 'store/authentication/selectors';
import Modal, { ModalHeader } from '..';
import { useSelector } from 'react-redux';
import { CustomizeFieldOptionsLink } from './CustomizeFieldOptionsLink';
import { getFieldOption, getValue, mapOptions } from './helpers';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'utility/fieldHelpers';
import {
  RequiredSwitch,
  StyledModalBody,
  StyledModalFooter,
  StyledSelect,
} from './styles';
import { Notice } from 'pages/Settings/pages/PrivacySettings/Privacy/styles';
import { useFlashTransition } from 'hooks/useFlashState';
import { StyledTextAreaInput } from 'components/GenericWizard/styles';
import Select from 'components/Inputs/Select';
import { DESCRIPTION_VISIBILITY } from 'utility/constants';

// currently not supporting this meta values in activity fields
const META_TO_EXCLUDE = ['isMasked', 'isMarkdown'];

export default function CustomFieldChooserModal({
  onHide,
  onSave,
  disabled,
  children,
  notice,
  label,
  context = EMPTY_OBJECT,
  modelsOptions = EMPTY_ARRAY,
  onGetRawFieldOptions,
  objectId = null,
  loading,
  isActivityField,
  enableReadonlyToggle = false,
  ...others
}) {
  const {
    field,
    title,
    displayNamePrefix,
    activityCustomObjects = [],
  } = context;
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [modelsVal, setModelsVal] = useState(null);
  const [fieldVal, setFieldVal] = useState(null);
  const [fieldName, setFieldName] = useField(
    () => (label ? label : fieldVal ? fieldVal.label : ''),
    [fieldVal]
  );
  const [fieldDescription, setFieldDescription] = useField(
    () => field?.description ?? fieldVal?.item?.description ?? '',
    [fieldVal, field]
  );

  const [fieldDescriptionVisibility, setFieldDescriptionVisibility] = useField(
    () =>
      field?.descriptionVisibility ??
      fieldVal?.item?.descriptionVisibility ??
      DESCRIPTION_VISIBILITY[0].value,
    [fieldVal, field]
  );
  const [fieldAPIName, setFieldAPIName] = useState(field?.name || '');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldReadonly, setFieldReadonly] = useState(
    field?.meta?.isReadonly ?? false
  );
  const contactsAccess = useSelector(getContactsAccess);
  const customObjectsSettingsAccess = useSelector(
    getCustomObjectsSettingsAccess
  );

  const showReadonlyToggle = enableReadonlyToggle && fieldVal;

  useEffect(() => {
    if (objectId && modelsOptions) {
      setModelsVal(modelsOptions.find(({ value }) => value === objectId));
    }
  }, [objectId, modelsOptions, setModelsVal]);

  useEffect(() => {
    if (field) {
      setModelsVal(
        getValue(
          field.customObjectField.customObject.id,
          mapOptions(modelsOptions)
        )
      );
    }
  }, [field, modelsOptions]);

  const { value: fieldOptions = EMPTY_ARRAY, loading: fieldsLoading } =
    useAsync(async () => {
      let options = EMPTY_ARRAY;
      if (modelsVal && modelsOptions) {
        // getting the raw options moved to buider so options can be cached between differen modal openings
        options = await onGetRawFieldOptions(modelsVal, modelsOptions, field);
        if (isPipelineObject(modelsVal.object)) {
          options = options.reduce((acc, category) => {
            const optionsWithoutActualCloseDate = category.options.filter(
              (f) => !isActualCloseDateField(f.item)
            );

            if (optionsWithoutActualCloseDate.length) {
              acc.push({ ...category, options: optionsWithoutActualCloseDate });
            }

            return acc;
          }, []);
        }

        if (field) {
          // when creating it's field.id but is customObjectField after creation
          setFieldVal(getFieldOption(field.customObjectField.id, options));
          setFieldRequired(field.isRequired);
        }
      }
      return options;
    }, [modelsVal, modelsOptions, field]);

  const [errors, setErrors] = useState({});
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const handleSave = async () => {
    try {
      setSubmitting(true);
      let { item: newField } = fieldVal;
      const meta = { ...newField.meta };
      META_TO_EXCLUDE.forEach((key) => delete meta[key]);
      // pull cols meta from activity fields (field) meta instead of field meta (newField)
      const { meta: { cols } = {} } =
        isActivityField && field ? field : newField;
      if (fieldReadonly) {
        meta.isReadonly = true;
      } else {
        delete meta.isReadonly;
      }
      const object = getValue(modelsVal.value, modelsOptions);
      newField = {
        ...newField,
        meta: {
          ...meta,
          cols,
        },
        displayName: fieldName,
        name: isActivityField ? fieldAPIName : undefined,
        isDefault: true,
        isRequired: fieldRequired,
        description: fieldDescription,
        descriptionVisibility: fieldDescriptionVisibility,
        object: object.id,
        orgField: field || null, // pass the original field back so we can correctly update it
      };
      const { success, error } = await onSave(newField, field?.id);
      if (success) {
        onHide();
      }
      if (error?.name) {
        setErrors((prev) => ({ ...prev, name: error.name }));
        flashErrorMessage(error.name);
      }
      if (error?.description) {
        setErrors((prev) => ({ ...prev, description: error.description }));
        flashErrorMessage(error.description);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const categorizedModelsOptions = useMemo(() => {
    return (
      modelsOptions?.reduce(
        (result, item) => {
          const key = activityCustomObjects.length
            ? activityCustomObjects.find((x) => x.id === item.value)
            : 1;
          return result[+!key].options.push(item) && result;
        },
        [
          {
            label: t('Associated Objects'),
            options: [],
          },
          {
            label: t('Other Objects'),
            options: [],
          },
        ]
      ) ?? []
    ).filter((item) => item.options.length);
  }, [activityCustomObjects, modelsOptions, t]);

  const handleModelsOnChange = (option) => {
    setModelsVal(option);
    setFieldVal(null);
  };

  const handleFieldOnChange = (option) => {
    setFieldVal(option);
  };

  const handleFieldNameOnChange = (val) => {
    setFieldName(val);
  };

  const handleFieldRequiredChange = (e) => {
    setFieldRequired(e.target.checked);
  };

  const handleAPINameOnChange = (val) => {
    setFieldAPIName((val || '').toLowerCase());
    cancelFlash();
    setErrors((prev) => ({ ...prev, name: undefined }));
  };

  const handleExit = () => {
    onHide();
  };

  const options = DESCRIPTION_VISIBILITY.map(({ value, label }) => ({
    value: value,
    label: label(t),
  }));

  const canEditFieldOptions = useMemo(() => {
    if (modelsVal) {
      if (isClientObject(modelsVal.object)) {
        return contactsAccess.object_settings.customize_fields.edit;
      } else {
        return customObjectsSettingsAccess[modelsVal.object.id].customizeFields
          .edit;
      }
    } else {
      return true;
    }
  }, [contactsAccess, customObjectsSettingsAccess, modelsVal]);

  return (
    <Modal onHide={handleExit} {...others}>
      <ModalHeader onClickClose={handleExit}>
        {title || t('Choose Custom Field')}
      </ModalHeader>
      {notice && <Notice>{notice}</Notice>}
      <StyledModalBody>
        {!objectId && (
          <StyledSelect
            label={t('Choose Object')}
            options={categorizedModelsOptions}
            value={modelsVal}
            onChange={handleModelsOnChange}
            menuInline
            fullWidth
            placeholder={t('Choose Object')}
            disabled={!!field}
            loadItems={loading}
          />
        )}
        <>
          {modelsVal && (
            <StyledSelect
              label={t('Choose Field')}
              options={fieldsLoading ? [] : fieldOptions}
              value={fieldVal}
              onChange={handleFieldOnChange}
              menuInline
              fullWidth
              placeholder={t('Choose Field')}
              disabled={!!field}
              loadItems={fieldsLoading}
            />
          )}

          {fieldVal && (
            <TextInput
              label={[displayNamePrefix, t('Display Name')]
                .filter(Boolean)
                .join(' ')}
              placeholder={[displayNamePrefix, t('Display Name')]
                .filter(Boolean)
                .join(' ')}
              value={fieldName}
              onChange={handleFieldNameOnChange}
              error={fieldName === ''}
              margin={3}
            />
          )}
          {isActivityField && field?.name ? (
            <TextInput
              label={t('Field API Name')}
              placeholder={t('Enter Field API Name')}
              value={fieldAPIName}
              onChange={handleAPINameOnChange}
              error={!!errors.name}
              validate={errors.name && { message, showMessage }}
              margin={3}
            />
          ) : null}
          <StyledTextAreaInput
            label={t('Description')}
            enableResize={false}
            value={fieldDescription}
            placeholder={t('Enter Description')}
            onChange={setFieldDescription}
            validate={{
              message: errors?.description,
              showMessage: errors?.description,
            }}
            error={errors?.description}
            margin={true}
            fullHeight={true}
            rows={5}
          />
          <Select
            fullWidth
            menuInline
            label={t('Description Visibility')}
            options={options}
            value={fieldDescriptionVisibility}
            onChange={(c) => setFieldDescriptionVisibility(c.value)}
            margin={true}
          />
          {fieldVal &&
            !isOwnerField(fieldVal.item) &&
            !isStageField(fieldVal.item) && (
              <RequiredSwitch
                data-qa="activity-required-toggle"
                label={t('Field is Required')}
                textPlacement="regular-top"
                checked={fieldRequired}
                value="true"
                onChange={handleFieldRequiredChange}
                disabled={field && field.isHidden}
                enableBottomMargin={showReadonlyToggle}
              />
            )}

          {showReadonlyToggle && (
            <Switch
              data-qa="activity-readonly-toggle"
              label={t('Informational Field (Readonly)')}
              checked={fieldReadonly}
              textPlacement="regular-top"
              onChange={(ev) => setFieldReadonly(ev.target.checked)}
              tooltip={
                fieldReadonly && fieldRequired
                  ? t(
                      "When field is empty, field will be editable because it's required."
                    )
                  : undefined
              }
            />
          )}

          {Boolean(modelsVal && fieldVal) &&
            canEditOptionsForActivity(fieldVal.item) &&
            canEditFieldOptions && (
              <CustomizeFieldOptionsLink
                model={modelsVal.object}
                field={fieldVal.item}
              />
            )}
        </>
      </StyledModalBody>
      <StyledModalFooter showBorder={false}>
        <Button
          onClick={handleExit}
          disabled={submitting}
          variant="text"
          color="blue"
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={submitting || !(fieldVal && fieldName !== '')}
        >
          {t('Save')}
        </Button>
      </StyledModalFooter>
    </Modal>
  );
}
